import { memo } from "react";
import CellButton from "@/components/CellButton";
import { TimerSelect } from "@/components/property-selectors/TimerSelect";
import { useActiveTimer } from "@/queries/timer-queries";
import type { Attribute } from "@/types/attribute";
import type { ColumnDef } from "../../ColumnModel";
import type { Row } from "../../RowModel";

const getSortValue = ({ row }: { row: Row }) => {
    return row.data.title; // todo: implement
};

const getGroupHeader = () => {
    throw new Error("Not implemented. Custom attributes cannot be grouped for now.");
};

const TimerCell = memo(function TimerCell({
    row,
    attribute,
    runningTaskId,
    runningAttributeId,
    runningSince,
}: {
    row: Row;
    attribute: Attribute;
    runningTaskId: number | null;
    runningAttributeId: number | null;
    runningSince: Date | null;
}) {
    const customFieldValues = row.data.customAttributeValues || []; // this can be null for new rows
    const customFieldValue = customFieldValues.find((x: any) => x.id === attribute.id);
    const isRunningHere = runningTaskId === row.data.id && runningAttributeId === attribute.id;

    return (
        <TimerSelect
            taskId={row.data.id}
            customAttributeId={attribute.id as number}
            value={customFieldValue?.value ?? null}
            runningSince={isRunningHere ? runningSince : null}
            trigger={(content) => (
                <CellButton className="tabular-nums text-sm" disabled={row.data.isNewlyCreated}>
                    {content}
                </CellButton>
            )}
        />
    );
});

export default function useTimerColumnBuilder() {
    // Unlike the other custom-attribute cells this one does not fan out over
    // useBulkTargetIds. "Set this duration on the selected rows" was meaningful;
    // "start a timer on the selected rows" is not, and only one may run anyway.
    const { data: active } = useActiveTimer();

    return {
        build: (isVisible: boolean, attribute: Attribute): ColumnDef => {
            return {
                id: attribute.id.toString(),
                header: attribute.name,
                isVisible: isVisible,
                cssColDef: "120px",
                isRight: false,
                isReadOnly: false,
                getSortValue: getSortValue,
                getGroupHeader: getGroupHeader,
                cell: ({ row }: { row: Row }) => (
                    <TimerCell
                        row={row}
                        attribute={attribute}
                        runningTaskId={active?.taskId ?? null}
                        runningAttributeId={active?.customAttributeId ?? null}
                        runningSince={active?.startedAt ?? null}
                    />
                ),
            };
        },
    };
}
